import type { SchemaType } from '@based/schema'
import { SchemaToType } from './utils'

export const SERVICE_PROVIDER_FEATURES = {
  overview: 'overview',
  events: 'events',
  developmentAreas: 'developmentAreas',
  concepts: 'concepts',
  initiatives: 'initiatives',
  knowledgeHub: 'knowledgeHub',
  current: 'current',
} as const

export type ServiceProviderFeatures = keyof typeof SERVICE_PROVIDER_FEATURES

export const REGION_FEATURES = {
  overview: 'overview',
  discussion: 'discussion',
  initiatives: 'initiatives',
  current: 'current',
  knowledgeHub: 'knowledgeHub',
  municipalities: 'municipalities',
  subsidies: 'subsidies',
  experts: 'experts',
} as const

export type RegionFeatures = keyof typeof REGION_FEATURES

export const MUNICIPALITY_FEATURES = {
  overview: 'overview',
  discussion: 'discussion',
  initiatives: 'initiatives',
  current: 'current',
  locations: 'locations',
  knowledgeHub: 'knowledgeHub',
  stepByStep: 'stepByStep',
  subsidies: 'subsidies',
  experts: 'experts',
} as const

export type MunicipalityFeatures = keyof typeof MUNICIPALITY_FEATURES

/** All geography tab identifier values (region ∪ municipality). */
export const GEOGRAPHY_PAGE_TAB_IDENTIFIERS = {
  ...REGION_FEATURES,
  ...MUNICIPALITY_FEATURES,
} as const

export type GeographyPageFeatures = RegionFeatures | MunicipalityFeatures

export const HOUSING_FORM_FEATURES = {
  overview: 'overview',
  discussion: 'discussion',
  initiatives: 'initiatives',
  plots: 'plots',
  knowledgeHub: 'knowledgeHub',
  current: 'current',
  popularHousingForms: 'popularHousingForms',
  experts: 'experts',
} as const

export type HousingFormFeatures = keyof typeof HOUSING_FORM_FEATURES

export const FEATURE_TOGGLE_IDENTIFIERS = {
  ...SERVICE_PROVIDER_FEATURES,
  ...REGION_FEATURES,
  ...MUNICIPALITY_FEATURES,
  ...HOUSING_FORM_FEATURES,
} as const

export type FeatureToggleIdentifier = keyof typeof FEATURE_TOGGLE_IDENTIFIERS

export const FEATURE_TOGGLE_ENTITY_TYPES = {
  serviceProvider: 'serviceProvider',
  region: 'region',
  municipality: 'municipality',
  housingForm: 'housingForm',
} as const

export type FeatureToggleEntityType = keyof typeof FEATURE_TOGGLE_ENTITY_TYPES

export const FEATURE_TOGGLE_ENTITY_FEATURES = {
  serviceProvider: SERVICE_PROVIDER_FEATURES,
  region: REGION_FEATURES,
  municipality: MUNICIPALITY_FEATURES,
  housingForm: HOUSING_FORM_FEATURES,
} as const satisfies Record<
  FeatureToggleEntityType,
  Record<string, FeatureToggleIdentifier>
>

export const featureToggle = {
  uuid: { type: 'alias', format: 'UUID' },
  identifier: Object.values(FEATURE_TOGGLE_IDENTIFIERS),
  description: { type: 'string' },
  entityType: Object.values(FEATURE_TOGGLE_ENTITY_TYPES),
  enabled: { type: 'boolean' },
  serviceProviders: {
    items: { ref: 'serviceProvider', prop: 'featureToggles' },
  },
  regions: {
    items: { ref: 'region', prop: 'featureToggles' },
  },
  municipalities: {
    items: { ref: 'municipality', prop: 'featureToggles' },
  },
  housingForms: {
    items: { ref: 'housingForm', prop: 'featureToggles' },
  },
  createdAt: { type: 'timestamp', on: 'create' },
  updatedAt: { type: 'timestamp', on: 'update' },
} satisfies SchemaType

export type FeatureToggle = SchemaToType<typeof featureToggle> & {
  id: number
}
